import { useEffect, useRef, useState } from "react";
import {
  Bike,
  CheckCircle2,
  Clock,
  Package,
  PackageCheck,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  PARTNER_FILTER_ORDER,
  type PartnerOrderFilter,
  type PartnerOrderStage,
  type PartnerStageStats,
} from "../../lib/partnerOrders";
import { useI18n, type TranslationKey } from "../../i18n";

const STAGE_META: Record<
  PartnerOrderStage,
  { icon: LucideIcon; countKey: keyof PartnerStageStats }
> = {
  pending: { icon: Clock, countKey: "pending" },
  processing: { icon: Package, countKey: "processing" },
  ready: { icon: PackageCheck, countKey: "ready" },
  in_delivery: { icon: Bike, countKey: "in_delivery" },
  completed: { icon: CheckCircle2, countKey: "completed" },
  cancelled: { icon: XCircle, countKey: "cancelled" },
};

/** Full stage labels — the strip wraps, so nothing gets cut off. */
const STAGE_LABEL_KEY: Record<PartnerOrderFilter, TranslationKey> = {
  pending: "orders.stages.pending",
  processing: "orders.stages.processing",
  ready: "orders.stages.ready",
  in_delivery: "orders.stages.in_delivery",
  completed: "orders.stages.completed",
  cancelled: "orders.stages.cancelled",
  all: "orders.stages.all",
};

/** Count that animates when the value changes — mirrors the HQ live flow. */
function AnimatedCount({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    const diff = value - prev;
    if (diff === 0) {
      setDisplay(value);
      return;
    }
    const duration = 380;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(prev + diff * eased));
      if (progress < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    prevRef.current = value;
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [value]);

  return <>{display}</>;
}

interface OrdersStepStripProps {
  value: PartnerOrderFilter;
  onChange: (next: PartnerOrderFilter) => void;
  stats?: PartnerStageStats;
  /** Total orders across all stages (shown on the All chip). */
  allCount?: number;
}

export function OrdersStepStrip({
  value,
  onChange,
  stats,
  allCount,
}: OrdersStepStripProps) {
  const { t } = useI18n();

  return (
    <div className="orders-step-strip" role="tablist" aria-label={t("orders.stagesAria")}>
      {PARTNER_FILTER_ORDER.map((key) => {
        const active = value === key;
        if (key === "all") {
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`orders-step-chip ${active ? "orders-step-chip--active" : ""}`}
              onClick={() => onChange("all")}
            >
              <span className="orders-step-chip__label">
                {t(STAGE_LABEL_KEY.all)}
              </span>
              {allCount != null ? (
                <span
                  className={`orders-step-chip__count ${
                    allCount === 0 ? "orders-step-chip__count--muted" : ""
                  }`}
                >
                  <AnimatedCount value={allCount} />
                </span>
              ) : null}
            </button>
          );
        }

        const meta = STAGE_META[key as PartnerOrderStage];
        const Icon = meta.icon;
        const count = stats ? stats[meta.countKey] : null;
        const attention = key === "pending" && (count ?? 0) > 0;

        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            className={`orders-step-chip ${active ? "orders-step-chip--active" : ""} ${
              attention ? "orders-step-chip--attention" : ""
            }`}
            onClick={() => onChange(key)}
          >
            <span className="orders-step-chip__icon" aria-hidden>
              <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
            <span className="orders-step-chip__label">
              {t(STAGE_LABEL_KEY[key])}
            </span>
            {count != null ? (
              <span
                className={`orders-step-chip__count ${
                  count === 0 ? "orders-step-chip__count--muted" : ""
                }`}
              >
                <AnimatedCount value={count} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
